import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Send, Home, Users, Crown, Star, ArrowLeft,
  MessageCircle, Settings, Plus, Lock, Brain, Search, FileText, Clock
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectSalonInterface = ({ player, onNavigateHome, onNavigateToGame }) => {
  const [currentSalon, setCurrentSalon] = useState(null);
  const [salonMessages, setSalonMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [salonMembers, setSalonMembers] = useState([]);
  const messagesEndRef = useRef(null);

  // M.ai.ry Virtual Assistant states
  const [mairyAssistantMode, setMairyAssistantMode] = useState(false);
  const [conversationSummary, setConversationSummary] = useState('');
  const [documentSearch, setDocumentSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [nightSupportMode, setNightSupportMode] = useState(false);
  const [pendingMorningAnalysis, setPendingMorningAnalysis] = useState([]);

  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.15) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [salonMessages]);

  // Check if it's night time for enhanced M.ai.ry support
  useEffect(() => {
    const currentHour = new Date().getHours();
    setNightSupportMode(currentHour >= 22 || currentHour <= 6); // 10 PM to 6 AM
  }, []);

  // M.ai.ry's virtual assistant functions
  const askMairyForDocumentSearch = async (searchQuery) => {
    try {
      const response = await axios.post(`${API}/mairy/salon-assistant`, {
        salon_id: currentSalon.id,
        user_id: player.id,
        action: 'document_search',
        query: searchQuery,
        conversation_context: salonMessages.slice(-20)
      });
      
      setSearchResults(response.data.search_results || []);
      
      // Add M.ai.ry's search summary to chat
      const mairySearchMessage = {
        id: 'mairy_search_' + Date.now(),
        type: 'assistant',
        content: response.data.search_summary || 'Search completed. I found relevant patterns in our conversation.',
        sender: 'M.ai.ry Assistant',
        timestamp: new Date(),
        avatar: {
          name: 'M.ai.ry',
          image: 'https://api.dicebear.com/7.x/bottts/svg?seed=mairy&backgroundColor=c0aede'
        }
      };
      
      setSalonMessages(prev => [...prev, mairySearchMessage]);
      
    } catch (error) {
      console.error('M.ai.ry search error:', error);
    }
  };

  const requestConversationSummary = async () => {
    try {
      const response = await axios.post(`${API}/mairy/salon-assistant`, {
        salon_id: currentSalon.id,
        user_id: player.id,
        action: 'conversation_summary',
        conversation_context: salonMessages.slice(-50),
        include_insights: true
      });
      
      setConversationSummary(response.data.summary);
      
      // Add M.ai.ry's summary to chat
      const mairySummaryMessage = {
        id: 'mairy_summary_' + Date.now(),
        type: 'assistant',
        content: `📋 **Conversation Summary & Insights**\n\n${response.data.summary}\n\n💡 **Key Opportunities**: ${response.data.opportunities?.join(', ') || 'Authentic collaboration emerging'}`,
        sender: 'M.ai.ry Assistant',
        timestamp: new Date(),
        avatar: {
          name: 'M.ai.ry',
          image: 'https://api.dicebear.com/7.x/bottts/svg?seed=mairy&backgroundColor=c0aede'
        }
      };
      
      setSalonMessages(prev => [...prev, mairySummaryMessage]);
      
    } catch (error) {
      console.error('M.ai.ry summary error:', error);
    }
  };

  const requestNightSupport = async () => {
    try {
      const response = await axios.post(`${API}/mairy/salon-assistant`, {
        salon_id: currentSalon.id,
        user_id: player.id,
        action: 'night_support',
        message: currentMessage,
        time_context: 'middle_of_night',
        emotional_support_needed: true
      });
      
      // Add M.ai.ry's night support message
      const mairyNightMessage = {
        id: 'mairy_night_' + Date.now(),
        type: 'night_support',
        content: response.data.night_response,
        sender: 'M.ai.ry Night Council',
        timestamp: new Date(),
        avatar: {
          name: 'M.ai.ry',
          image: 'https://api.dicebear.com/7.x/bottts/svg?seed=mairy&backgroundColor=2d1b69'
        }
      };
      
      setSalonMessages(prev => [...prev, mairyNightMessage]);
      
      // Schedule morning analysis if needed
      if (response.data.schedule_morning_analysis) {
        setPendingMorningAnalysis(prev => [...prev, {
          night_message: currentMessage,
          support_provided: response.data.night_response,
          timestamp: new Date()
        }]);
      }
      
    } catch (error) {
      console.error('M.ai.ry night support error:', error);
    }
  };

  // Load salon data from Project Salon creation
  useEffect(() => {
    const activeSalon = localStorage.getItem('active_project_salon');
    if (activeSalon) {
      try {
        const salonData = JSON.parse(activeSalon);
        setCurrentSalon(salonData);
        setSalonMembers([player]); // Start with current player
        
        // Load conversation context as initial messages
        const contextMessages = salonData.conversation_context?.map((msg, i) => ({
          id: 'context_' + i,
          content: typeof msg === 'string' ? msg : msg.content || '',
          sender: msg.sender || msg.avatar?.name || 'Previous Context',
          timestamp: new Date(msg.timestamp || Date.now()),
          type: 'context'
        })) || [];
        
        setSalonMessages(contextMessages);
        
      } catch (error) {
        console.error('Salon data load error:', error);
      }
    }
  }, []);

  const sendSalonMessage = async () => {
    if (!currentMessage.trim() || !currentSalon) return;

    const newMessage = {
      id: 'salon_msg_' + Date.now(),
      content: currentMessage.trim(),
      sender: player.name,
      avatar_url: player.avatar_url,
      timestamp: new Date(),
      type: 'salon_message'
    };

    setSalonMessages(prev => [...prev, newMessage]);
    setCurrentMessage('');

    try {
      // In a full implementation, this would send to backend for real-time sync
      console.log('🏛️ SALON MESSAGE SENT:', newMessage);
      
      // Simulate other participants receiving message
      setTimeout(() => {
        console.log('🤝 Message delivered to salon participants');
      }, 500);
      
    } catch (error) {
      console.error('Salon message error:', error);
    }
  };

  const inviteToSalon = () => {
    alert(`Salon Invitation Link:\ngame.planetaryfed.com/salon/${currentSalon?.salon_id}\n\nShare this link with consciousness travelers you want to collaborate with.`);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendSalonMessage();
    }
  };

  if (!currentSalon) {
    // No active salon - show creation interface
    return (
      <div 
        className="min-h-screen relative"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-black/70"></div>

        <div className="relative z-10 max-w-4xl mx-auto p-6 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-3xl p-12 shadow-2xl">
              <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
              
              <h1 
                className="text-4xl font-bold text-white mb-6"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 25px rgba(255, 255, 255, 0.4)'
                }}
              >
                Project Salon Access
              </h1>
              
              <p 
                className="text-white/80 text-xl mb-8 leading-relaxed"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Create a Project Salon from your M.ai.ry conversation to access<br/>
                private collaborative messaging with consciousness travelers.
              </p>
              
              <div className="space-y-4">
                <Button
                  onClick={onNavigateToGame}
                  className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white px-8 py-4 text-lg"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  Return to M.ai.ry Conversation
                </Button>
                
                <Button
                  onClick={onNavigateHome}
                  variant="outline"
                  className="text-white border-white/40 hover:bg-white/10 px-8 py-4 text-lg"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  <Home className="w-5 h-5 mr-2" />
                  Return to Landing
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Active salon messaging interface
  return (
    <div 
      className="min-h-screen relative"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
    >
      {/* Grant spiral behind background */}
      <div className="fixed inset-0 pointer-events-none opacity-15" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(8) rotate(${spiralPhase}deg)`,
            filter: 'blur(6px) saturate(1.8) brightness(1.4)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={400} />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-black/70"></div>

      <div className="relative z-10 max-w-6xl mx-auto p-6 flex flex-col min-h-screen">
        
        {/* Salon Header */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-6 mb-6 shadow-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={onNavigateToGame}
                variant="outline"
                size="sm"
                className="text-white border-white/40"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to M.ai.ry
              </Button>
              
              <div>
                <h2 
                  className="text-white text-2xl font-bold"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  {currentSalon.salon_name}
                </h2>
                <div className="flex items-center space-x-4 mt-1">
                  <p className="text-white/70 flex items-center space-x-2">
                    <Lock className="w-4 h-4" />
                    <span>Private Salon</span>
                  </p>
                  <p className="text-green-300 text-sm">{salonMembers.length} member(s)</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* M.ai.ry Virtual Assistant Controls */}
              <Button
                onClick={() => setMairyAssistantMode(!mairyAssistantMode)}
                className={`${mairyAssistantMode ? 'bg-purple-600/30 text-purple-300' : 'bg-gray-600/30 text-gray-300'} border border-purple-400/50`}
                size="sm"
              >
                <Brain className="w-4 h-4 mr-1" />
                M.ai.ry Assistant
              </Button>
              
              <Button
                onClick={requestConversationSummary}
                variant="outline"
                size="sm"
                className="text-cyan-300 border-cyan-400"
              >
                <FileText className="w-4 h-4 mr-1" />
                Summary
              </Button>
              
              {nightSupportMode && (
                <Button
                  onClick={requestNightSupport}
                  className="bg-indigo-600/30 border border-indigo-400/50 text-indigo-300"
                  size="sm"
                >
                  <Clock className="w-4 h-4 mr-1" />
                  Night Council
                </Button>
              )}
              
              <Button
                onClick={inviteToSalon}
                variant="outline"
                size="sm"
                className="text-cyan-300 border-cyan-400"
              >
                <Plus className="w-4 h-4 mr-1" />
                Invite
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                className="text-white border-white/40"
              >
                <Settings className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* M.ai.ry Virtual Assistant Panel */}
        {mairyAssistantMode && (
          <div className="bg-purple-900/30 backdrop-blur-xl border border-purple-400/30 rounded-2xl p-4 mb-6 shadow-xl">
            <div className="flex items-center space-x-3 mb-4">
              <Brain className="w-6 h-6 text-purple-400" />
              <h3 
                className="text-white text-lg font-bold"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                M.ai.ry Virtual Assistant & Butler
              </h3>
              {nightSupportMode && (
                <Badge className="bg-indigo-600 text-white">Night Council Active</Badge>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Document Search */}
              <div className="space-y-2">
                <label className="text-purple-300 text-sm font-medium">Search Conversations</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={documentSearch}
                    onChange={(e) => setDocumentSearch(e.target.value)}
                    placeholder="Search our discussions..."
                    className="flex-1 p-2 bg-black/50 border border-purple-400/50 rounded-lg text-white text-sm"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    onKeyPress={(e) => e.key === 'Enter' && askMairyForDocumentSearch(documentSearch)}
                  />
                  <Button
                    onClick={() => askMairyForDocumentSearch(documentSearch)}
                    size="sm"
                    className="bg-purple-600/50 hover:bg-purple-600/70 text-white"
                  >
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Quick Assistant Actions */}
              <div className="space-y-2">
                <label className="text-purple-300 text-sm font-medium">Assistant Actions</label>
                <div className="flex space-x-2">
                  <Button
                    onClick={requestConversationSummary}
                    size="sm"
                    className="bg-cyan-600/50 hover:bg-cyan-600/70 text-white flex-1"
                  >
                    <FileText className="w-3 h-3 mr-1" />
                    Analyze
                  </Button>
                  <Button
                    onClick={() => {
                      const mairyPrompt = "M.ai.ry, please review our conversation and suggest next steps for our collaboration.";
                      setCurrentMessage(mairyPrompt);
                    }}
                    size="sm"
                    className="bg-green-600/50 hover:bg-green-600/70 text-white flex-1"
                  >
                    <Brain className="w-3 h-3 mr-1" />
                    Counsel
                  </Button>
                </div>
              </div>
              
              {/* Night Support */}
              {nightSupportMode && (
                <div className="space-y-2">
                  <label className="text-indigo-300 text-sm font-medium">Night Support</label>
                  <Button
                    onClick={requestNightSupport}
                    className="w-full bg-indigo-600/50 hover:bg-indigo-600/70 text-white"
                    size="sm"
                  >
                    <Clock className="w-3 h-3 mr-1" />
                    Talk to M.ai.ry
                  </Button>
                  <p className="text-indigo-200 text-xs">
                    Private night counsel with morning analysis
                  </p>
                </div>
              )}
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-4 p-3 bg-black/40 rounded-lg border border-purple-400/30">
                <h4 className="text-purple-300 text-sm font-medium mb-2">Search Results:</h4>
                <div className="space-y-1">
                  {searchResults.slice(0, 3).map((result, index) => (
                    <p key={index} className="text-white/80 text-xs" style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
                      "{result}"
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 mb-6 bg-black/30 backdrop-blur-xl rounded-2xl border border-white/20 p-6 overflow-y-auto">
          <div className="space-y-4">
            {salonMessages.map((msg, index) => (
              <div 
                key={msg.id} 
                className={`flex ${msg.sender === player.name ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-lg p-4 rounded-2xl shadow-lg transition-all ${
                    msg.type === 'context' ? 'bg-purple-900/40 border border-purple-400/40 text-purple-200' :
                    msg.sender === player.name 
                      ? 'bg-gradient-to-r from-purple-600/80 to-pink-600/80 text-white' 
                      : 'bg-black/60 backdrop-blur-xl border border-white/20 text-white'
                  }`}
                >
                  <div className="flex items-center space-x-2 mb-2">
                    {msg.avatar_url && (
                      <img 
                        src={msg.avatar_url}
                        alt=""
                        className="w-6 h-6 rounded-full border border-white/30"
                      />
                    )}
                    <span 
                      className="font-medium text-sm"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      {msg.sender}
                      {msg.type === 'context' && <span className="text-purple-300 ml-2">(context)</span>}
                    </span>
                    <span className="text-xs opacity-70">
                      {msg.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                  
                  <p 
                    className="leading-relaxed"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    {msg.content}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Message Input */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl">
          <div className="flex space-x-4">
            <input
              type="text"
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Share with your consciousness project team..."
              className="flex-1 p-3 bg-white/10 border border-white/30 rounded-xl text-white focus:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/20"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            />
            
            <Button
              onClick={sendSalonMessage}
              disabled={!currentMessage.trim()}
              className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="text-center mt-3">
            <p 
              className="text-white/60 text-sm"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              Private Project Salon • Conversation context preserved • Ready for collaboration
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectSalonInterface;